import React, { useMemo } from "react";

import { mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type {
  EncodingProfileFormValues,
  QualitiesCost,
} from "./EncodingProfileEdit";

interface EncodingProfileSummaryProps {
  qualitiesCost: QualitiesCost | null;
}

function EncodingProfileSummary({
  qualitiesCost,
}: EncodingProfileSummaryProps): React.JSX.Element {
  const formValues = useWatch<EncodingProfileFormValues>();
  const { t } = useTranslation("editor-encoding-profile");

  const costData = useMemo(() => {
    const breakdown: { label: string; value: number }[] = [];
    let total = 0;

    Object.entries(formValues.videoFormats ?? {}).forEach(
      ([format, isSelected]) => {
        if (isSelected) {
          const formatCost =
            qualitiesCost?.video[format as keyof typeof qualitiesCost.video] ??
            0;
          total += formatCost;
          breakdown.push({
            label: `${t("encodingProfileForm.video")}: ${format}`,
            value: formatCost,
          });
        }
      },
    );

    Object.entries(formValues.audioFormats ?? {}).forEach(
      ([format, isSelected]) => {
        if (isSelected) {
          const formatCost =
            qualitiesCost?.audio[format as keyof typeof qualitiesCost.audio] ??
            0;
          total += formatCost;
          breakdown.push({
            label: `${t("encodingProfileForm.audio")}: ${format}`,
            value: formatCost,
          });
        }
      },
    );

    if (formValues.fast) {
      const fastAssetCost = qualitiesCost?.fastAsset ?? 0;
      total += fastAssetCost;
      breakdown.push({
        label: t("encodingProfileForm.fastAsset"),
        value: fastAssetCost,
      });
    }

    if (formValues.downloadToGo) {
      const downloadToGoCost = qualitiesCost?.downloadToGo ?? 0;
      total += downloadToGoCost;
      breakdown.push({
        label: t("encodingProfileForm.downloadToGo"),
        value: downloadToGoCost,
      });
    }

    return { total, breakdown };
  }, [formValues, qualitiesCost, t]);

  return (
    <div className="xl:sticky xl:top-[0.625rem] xl:self-start">
      <div className="text-overlay-foreground relative flex flex-col overflow-hidden rounded-xl bg-slate-900 p-[1.25rem] xl:min-w-[300px]">
        <div className="bg-primary/10 absolute top-0 right-0 -mt-32 -mr-32 size-64 blur-[100px]" />
        <div className="relative flex h-full flex-1 flex-col">
          <div className="mb-6 flex flex-col gap-[0.46875rem]">
            <span className="text-primary text-[0.625rem] font-semibold tracking-widest uppercase">
              {t("encodingProfileForm.costMultiplier")}
            </span>
            <span className="text-[2.34375rem] font-semibold tracking-widest">
              x{costData.total}
            </span>
          </div>
          <div className="mb-[0.9375rem] hidden flex-1 xl:!flex xl:flex-col xl:gap-[0.625rem]">
            <div className="flex items-center gap-[0.3125rem] border-b border-white/10 pb-[0.3125rem]">
              <span className="text-[0.625rem] font-medium tracking-widest text-white/40 uppercase">
                {t("encodingProfileForm.multiplierBreakdown")}
              </span>
            </div>
            <div className="gap-[0.46875rem]">
              {costData.breakdown.map((item) => (
                <div
                  key={item.label}
                  className="group flex items-center justify-between"
                >
                  <div className="flex items-center gap-[0.46875rem]">
                    <div className="bg-primary/40 group-hover:bg-primary size-1.5 rounded-full transition-colors" />
                    <span className="group-hover:text-overlay-foreground text-[0.703125rem] text-white/60 transition-colors">
                      {item.label}
                    </span>
                  </div>
                  <span className="text-primary font-mono text-[0.703125rem]">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-auto hidden border-t border-white/10 pt-3.75 xl:block!">
            <div className="mb-1.25 flex items-center justify-between">
              <span className="text-[0.625rem] font-medium uppercase">
                {t("encodingProfileForm.totalMultiplier")}
              </span>
              <span className="text-primary text-[1.171875rem] font-semibold tracking-widest">
                x{costData.total}
              </span>
            </div>
          </div>
          <a
            className="hover:text-primary flex items-center gap-1.25 text-[0.625rem] text-white/40 transition-colors"
            href={t("encodingProfileForm.learnMoreCostMultiplier.link")}
            rel="noreferrer"
            target="_blank"
          >
            <Icon path={mdiInformationOutline} size={0.9} />
            {t("encodingProfileForm.learnMoreCostMultiplier.label")}
          </a>
        </div>
      </div>
    </div>
  );
}

export default EncodingProfileSummary;
